/**
 * What a realm grants, and who it actually reaches.
 *
 * The Privacy Realms page answered the second question wrongly. It grouped
 * recipients by `ep.privacy_realm_id` — the LEGACY PRIMARY POINTER, one realm
 * per grant — but a grant reaches a realm through its attachments, and an EP
 * can be attached to several (`ep.realms`). Every realm that was attached but
 * not primary counted zero recipients and rendered as "Unused", including the
 * `→ name` realms node-sharing mints. The numbers were wrong, not just terse,
 * which is why the page could not be read.
 *
 * It also only counted `app`, `friend` and `group`, so a realm shared with an
 * automation, a plugin or the public said "Unused" too.
 */

import { plural } from '../nucleus/format';

/** Entity type → the word a person would use. Every type gets one: an
 *  automation reading your data is a recipient, whatever the model calls it. */
export const KIND = {
  friend: 'person',
  app: 'app',
  group_member: 'group',
  group_connection: 'group',
  group: 'group',
  public: 'public',
  automation: 'automation',
  plugin_execution: 'plugin',
  plugin_delegation: 'plugin',
  sub_entity: 'app',
};

export const KIND_ORDER = ['person', 'app', 'group', 'automation', 'plugin', 'public', 'other'];

export function kindOf(ep) {
  return KIND[ep?.entity_type] || 'other';
}

function isExpired(expiresAt) {
  return !!expiresAt && new Date(expiresAt) < new Date();
}

/** Is this grant live? A revoked or expired EP grants nothing, so it is not a
 *  recipient — counting it would overstate exposure on the page you open to
 *  check exposure. */
export function isLiveGrant(ep) {
  return !!ep && ep.is_active !== false && !ep.revoked_at && !isExpired(ep.expires_at);
}

/**
 * realmId → the live grants attached to it.
 *
 * Reads `ep.realms` (the attachments) and falls back to `privacy_realm_id` only
 * for a grant with NO attachments — a legacy EP that predates the backfill and
 * still draws its ceiling from the primary pointer. That fallback is the same
 * one the authorization checker makes, so the page and enforcement agree.
 */
export function recipientsByRealm(grants) {
  const map = {};
  const push = (realmId, ep) => {
    if (!realmId) return;
    const key = String(realmId);
    if (!map[key]) map[key] = [];
    if (!map[key].some(x => x.id === ep.id)) map[key].push(ep);
  };

  (grants || []).filter(isLiveGrant).forEach(ep => {
    const attached = Array.isArray(ep.realms) ? ep.realms.filter(r => r && r.id) : [];
    if (attached.length) attached.forEach(r => push(r.id, ep));
    else push(ep.privacy_realm_id, ep);
  });
  return map;
}

/** A grant, reduced to what the page renders. */
export function toRecipient(ep) {
  return {
    id: ep.id,
    label: ep.entity_label || ep.entity_name || 'Shared',
    kind: kindOf(ep),
    entity_type: ep.entity_type,
  };
}

// ── the verb palette ────────────────────────────────────────────────────────
// One colour per verb, in the list cell and in the panel alike. It is the only
// thing that tells a realm which READS from one which CHANGES things without
// reading a word, so a verb missing from this map falling back to green would
// be a lie — unknown verbs are treated as writes.
const TONE = {
  read: 'read', friends: 'read', groups: 'read', realms: 'read',
  edit: 'edit', edit_content: 'edit', modify: 'edit', propose: 'edit', write: 'edit',
  // belongs_to hands the from-node the to-node's audience, so inheriting is a
  // change to who can see something — not a read.
  inherit: 'edit',
  create: 'make', declare: 'make',
  delete: 'del',
  execute: 'run',
};
export function toneOf(verb) {
  return TONE[verb] || 'edit';
}

export const RESOURCE_LABEL = {
  nodes: 'Nodes',
  plugins: 'Plugins',
  discovery: 'Discovery',
  activity: 'Activity',
  automations: 'Automations',
  sub_entity: 'Sub-entities',
};
const RESOURCE_ORDER = Object.keys(RESOURCE_LABEL);

const VERB_LABEL = { edit_content: 'edit content', sub_entity: 'declare' };
function verbLabel(v) { return VERB_LABEL[v] || v; }

/* Items carry their ID as well as their label: the read view only needs the
   label, but the editor has to toggle the thing the label stands for, and
   matching back by display name would break the moment two schemas share one. */
function scopeItems(resource, scope, ctx) {
  const s = scope || {};
  if (resource === 'nodes') {
    const out = Object.keys(s.schemas || {}).map(id => ({ id, label: ctx.schemaName(id), kind: 'schema' }));
    const n = (s.nodes || []).length;
    // Named nodes are not toggleable here — they were chosen one at a time on
    // the node itself, and a chip that dropped six of them at once would be a
    // much bigger act than it looks.
    if (n) out.push({ id: null, label: plural(n, 'node'), kind: 'nodes', fixed: true });
    return out;
  }
  if (resource === 'plugins') {
    const out = (s.plugin_ids || []).map(id => ({ id, label: ctx.pluginName(id), kind: 'plugin' }));
    // A refusal is part of the summary, not a footnote. A realm that allows
    // Google and refuses Drive summarised as plain "Google", which reads as
    // more access than was given — the summary erring in the one direction it
    // must not. Marked `fixed` because a tool limit is not dropped by a chip
    // click: it is authored per tool, and removing it here would widen a grant
    // in a control built for narrowing.
    let denied = 0;
    for (const modes of Object.values(s.tools || {})) {
      if (modes && typeof modes === 'object') {
        denied += Object.values(modes).filter(m => m === 'deny').length;
      }
    }
    if (denied) {
      out.push({ id: null, kind: 'tools', fixed: true,
                 label: `${plural(denied, 'tool')} refused` });
    }
    return out;
  }
  return [];
}

/** The id-keyed field a resource's scope narrows by, or null if it has none. */
export function scopeKeyFor(resource) {
  if (resource === 'nodes') return 'schemas';
  if (resource === 'plugins') return 'plugin_ids';
  return null;
}

/**
 * A ceiling blob → what to render, grouped the way the ceiling is stored.
 *
 *   [{ resource, label, rows: [{ verb, tone, items, unbounded }] }]
 *
 * `unbounded` is the scope that names neither schemas nor ids: it reaches
 * everything of that resource, now and in future, and is a different KIND of
 * grant rather than a longer list — which is why it renders as one alarming
 * chip instead of an enumeration nobody can check.
 */
export function summariseCeiling(ceiling, { schemas = [], plugins = [] } = {}) {
  const schemaById = new Map();
  (schemas || []).forEach(s => {
    if (s?.id) schemaById.set(String(s.id), s.schema_name || s.name || String(s.id));
  });
  const pluginById = new Map();
  (plugins || []).forEach(p => {
    if (p?.id) pluginById.set(String(p.id), p.name || String(p.id));
  });
  const ctx = {
    // Never render a raw uuid at a person; a schema we cannot resolve is still
    // a grant, so it is counted and named as unknown rather than dropped.
    schemaName: id => schemaById.get(String(id)) || 'unknown schema',
    pluginName: id => pluginById.get(String(id)) || 'unknown plugin',
  };

  const groups = [];
  const blob = ceiling || {};
  const resources = Object.keys(blob).sort(
    (a, b) => (RESOURCE_ORDER.indexOf(a) + 1 || 99) - (RESOURCE_ORDER.indexOf(b) + 1 || 99),
  );

  resources.forEach(resource => {
    const verbs = blob[resource];
    if (!verbs || typeof verbs !== 'object') return;
    const rows = Object.keys(verbs).sort().map(verb => {
      const items = scopeItems(resource, verbs[verb], ctx);
      return { verb, label: verbLabel(verb), tone: toneOf(verb), items, unbounded: items.length === 0 };
    });
    if (rows.length) {
      groups.push({ resource, label: RESOURCE_LABEL[resource] || resource, rows });
    }
  });
  return groups;
}

/** How many separate grants a ceiling holds — the number beside "Grants". */
export function grantCount(groups) {
  return groups.reduce((n, g) => n + g.rows.length, 0);
}

/**
 * The list cell: how much, never which.
 *
 * A ceiling with seventeen grants would be six lines in a table cell, every row
 * a different height, and the table stops being scannable — which was the whole
 * reason to leave cards behind. So the cell is capped and the panel carries the
 * detail.
 */
export function cellChips(groups, max = 3) {
  const rows = groups.flatMap(g => g.rows);
  // Everything unbounded, across more than one resource, is the transparent
  // ceiling. Listing "read all · edit all · delete all" buries the only fact
  // that matters about it.
  if (rows.length > 1 && rows.every(r => r.unbounded)) {
    return { chips: [{ tone: 'all', text: 'Everything, every node' }], rest: 0 };
  }
  const chips = rows.slice(0, max).map(r => ({
    tone: r.unbounded ? 'all' : r.tone,
    text: `${r.label.charAt(0).toUpperCase()}${r.label.slice(1)} · ${r.unbounded ? 'all' : r.items.length}`,
  }));
  return { chips, rest: Math.max(0, rows.length - max) };
}

// ── editing a ceiling ───────────────────────────────────────────────────────
// Every change returns a NEW blob. The panel holds the draft and nothing is
// written until Save, so an accidental click on a permission surface is undone
// by walking away rather than by a second correct click.

function cloneCeiling(ceiling) {
  return JSON.parse(JSON.stringify(ceiling || {}));
}

/** Turn one schema or plugin on or off for a verb. */
export function toggleGrantItem(ceiling, resource, verb, id) {
  const key = scopeKeyFor(resource);
  if (!key || id == null) return ceiling;
  const out = cloneCeiling(ceiling);
  const scope = ((out[resource] || {})[verb]) || {};
  out[resource] = out[resource] || {};
  out[resource][verb] = scope;

  if (key === 'schemas') {
    const map = scope.schemas || {};
    if (map[id]) delete map[id]; else map[id] = {};
    if (Object.keys(map).length) scope.schemas = map; else delete scope.schemas;
  } else {
    const list = scope[key] || [];
    scope[key] = list.includes(id) ? list.filter(x => x !== id) : [...list, id];
    if (!scope[key].length) delete scope[key];
  }

  /* A verb whose last item was switched off is REMOVED, not left empty —
     an empty scope means UNRESTRICTED, so leaving it behind would silently
     turn "they can read these two schemas" into "they can read everything".
     The one mistake this editor must not be able to make. */
  const s = out[resource][verb];
  // `tools` COUNTS AS A NARROWING. Left out of this list, a scope carrying only
  // per-tool limits — no plugin_ids, because the realm allows every plugin and
  // merely refuses two of Google's tools — read as "nothing narrowed", so the
  // verb was deleted and the limits went with it. An empty scope means
  // UNRESTRICTED, so that is the exact mistake the comment above forbids,
  // arriving through the one field the list had not been taught about.
  const stillNarrowed = ['schemas', 'nodes', 'plugin_ids', 'tools']
    .some(f => s[f] && (Array.isArray(s[f]) ? s[f].length : Object.keys(s[f]).length));
  if (!stillNarrowed) {
    delete out[resource][verb];
    if (!Object.keys(out[resource]).length) delete out[resource];
  }
  return out;
}

/** Grant or drop a whole verb without naming anything: unrestricted, or gone. */
export function setVerbUnbounded(ceiling, resource, verb, unbounded) {
  const out = cloneCeiling(ceiling);
  if (unbounded) {
    out[resource] = out[resource] || {};
    out[resource][verb] = {};
  } else if (out[resource]) {
    delete out[resource][verb];
    if (!Object.keys(out[resource]).length) delete out[resource];
  }
  return out;
}

/**
 * What the editor renders: every item the realm grants NOW, plus every item it
 * granted when the panel opened, each flagged on or off.
 *
 * Keeping switched-off items visible is what makes the change reviewable — a
 * chip that vanished on click would leave you unable to see what you just did,
 * let alone undo it before saving.
 */
export function editRows(original, draft, ctx) {
  const base = summariseCeiling(original, ctx);
  const now = summariseCeiling(draft, ctx);
  const byVerb = (groups) => {
    const m = new Map();
    groups.forEach(g => g.rows.forEach(r => m.set(`${g.resource}:${r.verb}`, { g, r })));
    return m;
  };
  const nowMap = byVerb(now);
  const keys = [...new Set([...byVerb(base).keys(), ...nowMap.keys()])];

  const groups = new Map();
  keys.forEach(key => {
    const [resource, verb] = key.split(':');
    const b = byVerb(base).get(key)?.r;
    const n = nowMap.get(key)?.r;
    const items = new Map();
    (b?.items || []).forEach(i => items.set(String(i.id), { ...i, on: false }));
    (n?.items || []).forEach(i => items.set(String(i.id), { ...i, on: true }));
    if (!groups.has(resource)) {
      groups.set(resource, { resource, label: RESOURCE_LABEL[resource] || resource, rows: [] });
    }
    groups.get(resource).rows.push({
      verb,
      label: VERB_LABEL[verb] || verb,
      tone: toneOf(verb),
      granted: !!n,
      unbounded: !!n?.unbounded,
      items: [...items.values()],
    });
  });
  const order = Object.keys(RESOURCE_LABEL);
  return [...groups.values()]
    .sort((a, b) => (order.indexOf(a.resource) + 1 || 99) - (order.indexOf(b.resource) + 1 || 99))
    .map(g => ({ ...g, rows: g.rows.sort((a, b) => a.verb.localeCompare(b.verb)) }));
}

/** Realms a lens keeps. `all` keeps everything; `none` is the to-do list. */
export function matchesLens(lens, { recipients, isSystem }) {
  if (lens === 'all') return true;
  if (lens === 'system') return isSystem;
  if (lens === 'none') return recipients.length === 0;
  return recipients.some(r => r.kind === lens);
}

/** Text search over the realm AND its recipients — "ink" should find every
 *  realm Ink can reach, not only the one called `→ Ink`. */
export function matchesSearch(term, { realm, recipients }) {
  const s = String(term || '').toLowerCase().trim();
  if (!s) return true;
  if ((realm.name || '').toLowerCase().includes(s)) return true;
  if ((realm.description || '').toLowerCase().includes(s)) return true;
  return recipients.some(r => (r.label || '').toLowerCase().includes(s));
}
